/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Story plaintext envelope (#240).
 *
 * The bytes that go through the F13 document-encryption path are not the
 * raw story markdown but a small JSON envelope `{v, title, content}` —
 * so the title rides *inside* the ciphertext (end-to-end encrypted, per
 * the #240 acceptance criterion "title sourced from decrypted story
 * data") instead of as cleartext server metadata.
 *
 * Both halves of the pipeline share this codec:
 *   - `storyprocessor.ts` (save flow) encodes before encrypting
 *   - `story.ts` (load flow) decodes after decrypting
 *
 * Decoding is tolerant of pre-#240 stories whose plaintext was the raw
 * content string: anything that is not a `{v: 1, ...}` envelope decodes
 * as `{title: '', content: <raw>}`.
 */

import { z } from 'zod';

/** Envelope format version — bump on structural change. */
export const STORY_PLAINTEXT_VERSION = 1;

export interface StoryPlaintext {
  title: string;
  content: string;
}

const StoryPlaintextSchema = z
  .object({
    v: z.literal(STORY_PLAINTEXT_VERSION),
    title: z.string(),
    content: z.string(),
  })
  .strict();

/**
 * Serialize title + content into the versioned JSON envelope that gets
 * encrypted as the story document.
 */
export const encodeStoryPlaintext = (plaintext: StoryPlaintext): string =>
  JSON.stringify({
    v: STORY_PLAINTEXT_VERSION,
    title: plaintext.title,
    content: plaintext.content,
  });

/**
 * Parse decrypted story bytes back into `{title, content}`.
 *
 * Legacy fallback: stories encrypted before #240 carried the raw content
 * string (usually not valid envelope JSON). Any input that does not
 * parse as a v1 envelope is returned as `{title: '', content: raw}` —
 * lossless for old data, and impossible to confuse with a real envelope
 * because encoding always emits the `v` discriminator.
 */
export const decodeStoryPlaintext = (raw: string): StoryPlaintext => {
  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(raw);
  } catch {
    return { title: '', content: raw };
  }
  const result = StoryPlaintextSchema.safeParse(parsedJson);
  if (!result.success) {
    return { title: '', content: raw };
  }
  return { title: result.data.title, content: result.data.content };
};
